/**
 * From raw rows to named columns, and from columns to a card's elements
 * (KPR-1899). The elements are `GET /api/collections/{id}/schema`'s, so an
 * element hidden from this person is simply not here.
 */

/** A cell as text: what it reads as in the spreadsheet. */
export function cellText(cell) {
	if (cell === null || cell === undefined) return '';
	if (typeof cell === 'string') return cell;
	if (cell.kind === 'number') return cell.text;
	if (cell.kind === 'boolean') return cell.value ? 'TRUE' : 'FALSE';
	if (cell.kind === 'date') return [cell.date, cell.time ? cell.time.slice(0, 5) : null].filter(Boolean).join(' ');
	return String(cell);
}

export const isBlankCell = cell => !cellText(cell).trim();

/** 0 -> A, 25 -> Z, 26 -> AA: the letters above a spreadsheet's columns. */
export function columnLetter(index) {
	let n = index + 1;
	let out = '';
	while (n > 0) {
		const rest = (n - 1) % 26;
		out = String.fromCharCode(65 + rest) + out;
		n = Math.floor((n - 1) / 26);
	}
	return out;
}

/**
 * The row that names the columns: the first with anything in it, past any
 * title lines. A hand-made sheet often opens with "Residents 2026" alone in A1,
 * so while a row nearby fills several columns, a row of one cell is not the
 * header. A sheet of one column has only rows of one cell, and takes the first.
 */
const HEADER_LOOKAHEAD = 20;

function findHeader(rawRows) {
	const filled = row => row.filter(cell => !isBlankCell(cell)).length;
	const first = rawRows.findIndex(row => filled(row) > 0);
	if (first < 0) return -1;
	const wide = rawRows.slice(first, first + HEADER_LOOKAHEAD).findIndex(row => filled(row) > 1);
	if (wide < 0) return first;
	// A header has rows under it; "Name," over one record is that record's header.
	return rawRows.slice(first + wide + 1).some(row => filled(row) > 0) ? first + wide : first;
}

/**
 * What is under the header is the rows. `chosenHeader` is the index the person
 * chose, when findHeader read the sheet wrong. A row keeps the number it has in the
 * file, which is how a person finds it. Blank rows drop out, and so do columns
 * with neither a name nor a value (a sheet's trailing commas). A column with
 * values and no name is Column D; a name used twice is told apart as Date,
 * Date (2).
 *
 * -> { headerRow, columns: [{ index, name }], rows: [{ number, cells }] }
 */
export function tabulate(rawRows, chosenHeader = null) {
	const headerAt = chosenHeader ?? findHeader(rawRows);
	if (headerAt < 0) return { headerRow: null, columns: [], rows: [] };
	const header = rawRows[headerAt];
	const rows = [];
	for (let i = headerAt + 1; i < rawRows.length; i += 1) {
		if (rawRows[i].some(cell => !isBlankCell(cell))) rows.push({ number: i + 1, cells: rawRows[i] });
	}
	const width = rows.reduce((most, row) => Math.max(most, row.cells.length), header.length);
	const seen = new Map();
	const columns = [];
	for (let index = 0; index < width; index += 1) {
		const named = cellText(header[index]).trim();
		if (named || rows.some(row => !isBlankCell(row.cells[index]))) {
			const base = named || `Column ${columnLetter(index)}`;
			const key = base.toLowerCase();
			const count = (seen.get(key) || 0) + 1;
			seen.set(key, count);
			columns.push({ index, name: count > 1 ? `${base} (${count})` : base });
		}
	}
	return { headerRow: headerAt + 1, columns, rows };
}

/** The rows that could name the columns, for the person to choose among: the first ten with anything in them. */
export function headerChoices(rawRows, limit = 10) {
	const out = [];
	for (let i = 0; i < rawRows.length && out.length < limit; i += 1) {
		const cells = rawRows[i].filter(cell => !isBlankCell(cell)).map(cellText);
		if (cells.length) {
			const words = cells.slice(0, 4).join(', ');
			out.push({ value: i + 1, label: `Row ${i + 1} · ${words.length > 60 ? `${words.slice(0, 59)}…` : words}` });
		}
	}
	return out;
}

/** Up to `limit` different values a column holds, for the person to recognise it. */
export function sampleValues(sheet, columnIndex, limit = 3) {
	const out = [];
	for (const row of sheet.rows) {
		const text = cellText(row.cells[columnIndex]).trim();
		if (text && !out.includes(text)) out.push(text);
		if (out.length >= limit) break;
	}
	return out;
}

export const elementLabel = el => String(el?.label || el?.name || '');

const WITHHELD_WORDS = {
	driven: 'keepr works this out',
	sequence: 'keepr numbers this',
	confirmations: 'keepr counts these',
	'repeating-group': 'a repeating group takes its entries in keepr',
	file: 'files are attached in keepr',
	see: 'you can see this but not change it',
};

function withheldWhy(el) {
	if (el.driven) return WITHHELD_WORDS.driven;
	if (el.sequence) return WITHHELD_WORDS.sequence;
	if (el.dataType === 'confirmations' || el.serverAssigned) return WITHHELD_WORDS.confirmations;
	if (el.dataType === 'repeating-group') return WITHHELD_WORDS['repeating-group'];
	if (el.dataType === 'file') return WITHHELD_WORDS.file;
	if (el.access === 'see') return WITHHELD_WORDS.see;
	return null;
}

/**
 * Which of a schema card's elements a column may fill.
 *   offered   a person can write it through ingest
 *   withheld  [{ element, why }]: not offered, and the page says why
 * A manual order is neither: it is a place in a list, not a value anyone holds.
 */
export function importableElements(card) {
	const offered = [];
	const withheld = [];
	(Array.isArray(card?.elements) ? card.elements : []).forEach(element => {
		if (element.rank) return;
		const why = withheldWhy(element);
		if (why) withheld.push({ element, why });
		else offered.push(element);
	});
	return { offered, withheld };
}

const fold = text => String(text || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

// "Weight (lb)" names a measurement and says its unit; "Total (USD)" a currency.
const SUFFIX = /^(.*\S)\s+\(([^()]+)\)$/;
const TAKES_SUFFIX = new Set(['measurement', 'currency']);

/**
 * The element a column's name matches, for a suggestion, with the unit or
 * currency a name carries. Case, spaces and punctuation do not count, so
 * "Date of birth", "date_of_birth" and "DateOfBirth" are one name.
 */
export function matchColumn(name, elements) {
	const wanted = fold(name);
	const exact = wanted ? elements.find(el => fold(elementLabel(el)) === wanted || fold(el.name) === wanted) : null;
	if (exact) return { element: exact, suffix: null };
	const parts = String(name).match(SUFFIX);
	if (parts) {
		const element = elements.find(el => TAKES_SUFFIX.has(el.dataType) && (fold(elementLabel(el)) === fold(parts[1]) || fold(el.name) === fold(parts[1])));
		if (element) return { element, suffix: parts[2].trim() };
	}
	return null;
}

/** The unit or currency a column's name carries for this element, however the element came to be chosen. */
export function columnSuffix(name, element) {
	if (!TAKES_SUFFIX.has(element?.dataType)) return null;
	const parts = String(name).match(SUFFIX);
	return parts ? parts[2].trim() : null;
}

/**
 * Suggestions for the columns not mapped yet: { [columnIndex]: { element name, suffix } }.
 * Offered, never applied, and each element once, to the first column that
 * names it. An element a column already fills is not offered again.
 */
export function suggestMapping(columns, elements, mapping = {}) {
	const taken = new Set(Object.values(mapping).map(entry => entry?.element).filter(Boolean));
	const out = {};
	columns.forEach(column => {
		if (mapping[column.index]?.element) return;
		const match = matchColumn(column.name, elements.filter(el => !taken.has(el.name)));
		if (!match) return;
		taken.add(match.element.name);
		out[column.index] = { element: match.element.name, suffix: match.suffix };
	});
	return out;
}

/**
 * Elements a row must carry that no column fills and no default will: the
 * person hears it while they can still map one. `requiredWhen` is the server's
 * to judge, row by row, in the check.
 */
export function unfilledRequired(elements, mapping) {
	const filled = new Set(Object.values(mapping).map(entry => entry?.element).filter(Boolean));
	return elements.filter(el => el.required
		&& !filled.has(el.name)
		&& el.defaultValue === undefined
		&& !el.defaultToToday
		&& !el.defaultToNow);
}
